import React, { useContext, useRef, useEffect, useState, useMemo, useCallback } from "react";
import SideBar from "../components/SideBar";
import mainStyle from "../styles/mainComponents.module.css";
import { CompanyContext } from "../features/CompanyContext";
import styles from "../styles/database.module.css";
import { useTranslation } from "react-i18next";
import useFetch from "../hooks/useFetch";
import ChartToImage from "../components/ChartToImage";
import { InfoPopup } from "../components/Popup";
import LoadingModal from "../components/LoadingModal";
import {
    downloadOrgReportWithProgress,
    triggerBlobDownload,
    filenameFromContentDisposition,
} from "../utils/downloadOrgReport";
import BetaIAPopup from "../components/Portals/BetaIAPopup";
import ExportExcelPopup from "../components/Portals/ExportExcelPopup";
import MultiTeamsReportPopup from "../components/Portals/MultiTeamsReportPopup";
import TeamSurveySelector from "../components/Database/TeamSurveySelector";
import {
    getLatestSurvey,
    resolveTeamSurveyPairs,
} from "../utils/multiTeamReport";

/** Normaliza idioma de encuesta a ES | EN */
const normalizeSurveyLang = (idioma) => {
    const raw = String(idioma || "").trim().toUpperCase();
    if (raw.startsWith("EN")) return "EN";
    if (raw.startsWith("ES")) return "ES";
    return null;
};

function Database() {
    const { selectedCompany } = useContext(CompanyContext);
    const { t, i18n } = useTranslation();
    const { data, execute } = useFetch();
    const [companies, setCompanies] = useState([]);
    const [selectedCompanies, setSelectedCompanies] = useState([]);
    const [selectedTeams, setSelectedTeams] = useState([]);
    const [selectedSurveys, setSelectedSurveys] = useState([]);
    const [companyColors, setCompanyColors] = useState({});
    const executeRef = useRef(execute);
    const [capturingChart, setCapturingChart] = useState(false);
    const [dashboardData, setDashboardData] = useState(null);
    const { data: data2, execute: execute2 } = useFetch();
    const [showInfoPopup, setShowInfoPopup] = useState(false);
    const [infoPopupMessage, setInfoPopupMessage] = useState("");
    const [barBlobEs, setBarBlobEs] = useState(null);
    const [barBlobEn, setBarBlobEn] = useState(null);
    const [radarBlob, setRadarBlob] = useState(null);
    const [indexBlobEs, setIndexBlobEs] = useState(null);
    const [indexBlobEn, setIndexBlobEn] = useState(null);
    const [capturingBarEs, setCapturingBarEs] = useState(false);
    const [capturingBarEn, setCapturingBarEn] = useState(false);
    const [capturingRadar, setCapturingRadar] = useState(false);
    const [capturingIndexEs, setCapturingIndexEs] = useState(false);
    const [capturingIndexEn, setCapturingIndexEn] = useState(false);
    const [loadingModalVisible, setLoadingModalVisible] = useState(false);
    const [loadingMessage, setLoadingMessage] = useState("");
    const [loadingDetail, setLoadingDetail] = useState("");
    const [loadingStep, setLoadingStep] = useState(0);
    const [loadingStepTotal, setLoadingStepTotal] = useState(0);
    const [betaIAPopupOpen, setBetaIAPopupOpen] = useState(false);
    const [exportExcelPopupOpen, setExportExcelPopupOpen] = useState(false);
    const [multiTeamsPopupOpen, setMultiTeamsPopupOpen] = useState(false);
    const [exportMenuOpen, setExportMenuOpen] = useState(false);
    const [exportMenuPanelStyle, setExportMenuPanelStyle] = useState({});
    const [orgReportUseAi, setOrgReportUseAi] = useState(() => {
        try {
            return localStorage.getItem("orgReportUseAi") !== "0";
        } catch {
            return true;
        }
    });
    const exportMenuRef = useRef(null);
    const exportMenuTriggerRef = useRef(null);
    const [manualTeamSelection, setManualTeamSelection] = useState(false);
    const multiTeamsExportRef = useRef(null);

    const findTeamById = (teamId) =>
        companies.flatMap((c) => c.equipos || []).find((t) => t.id_equipo === teamId);

    const isSingleCompanyMode = selectedCompanies.length === 1;
    const isCompanyOnlyMode =
        isSingleCompanyMode && !manualTeamSelection && selectedTeams.length === 0;

    /** Equipos efectivos: empresa entera (solo empresa) o selección manual. */
    const effectiveTeamIds = useMemo(() => {
        if (!isSingleCompanyMode) return selectedTeams;
        const company = companies.find((c) => c.id_empresa === selectedCompanies[0]);
        if (!company?.equipos?.length) return selectedTeams;
        if (isCompanyOnlyMode) return company.equipos.map((t) => t.id_equipo);
        return selectedTeams;
    }, [isSingleCompanyMode, isCompanyOnlyMode, selectedCompanies, selectedTeams, companies]);

    /** Pares equipo↔encuesta para informe multi-equipos (última encuesta por defecto). */
    const teamSurveyPairs = useMemo(() => {
        if (!isSingleCompanyMode) return [];
        return resolveTeamSurveyPairs(
            companies,
            selectedCompanies[0],
            effectiveTeamIds,
            selectedSurveys
        );
    }, [isSingleCompanyMode, selectedCompanies, effectiveTeamIds, selectedSurveys, companies]);

    /** Informe multi-equipos: solo empresa (todos los equipos) o ≥2 equipos elegidos a mano. */
    const canExportMultiTeams = useMemo(() => {
        if (!isSingleCompanyMode) return false;
        if (teamSurveyPairs.length < 2) return false;
        if (manualTeamSelection && selectedTeams.length < 2) return false;
        const companyId = selectedCompanies[0];
        const company = companies.find((c) => c.id_empresa === companyId);
        if (!company?.equipos?.length) return false;
        const teamIdsInCompany = new Set(company.equipos.map((t) => t.id_equipo));
        return teamSurveyPairs.every((p) => teamIdsInCompany.has(p.id_equipo));
    }, [isSingleCompanyMode, teamSurveyPairs, manualTeamSelection, selectedTeams, selectedCompanies, companies]);

    /** Leader Report: empresa entera (última encuesta por equipo) o un equipo con encuesta. */
    const canExportLeaderReport = useMemo(() => {
        if (!isSingleCompanyMode) return false;
        const companyId = selectedCompanies[0];
        const company = companies.find((c) => c.id_empresa === companyId);
        if (!company?.equipos?.length) return false;

        const teamIds = isCompanyOnlyMode
            ? company.equipos.map((t) => t.id_equipo)
            : selectedTeams;
        if (!teamIds.length) return false;

        const pairs = resolveTeamSurveyPairs(
            companies,
            companyId,
            teamIds,
            selectedSurveys
        );
        return pairs.length >= 1;
    }, [
        isSingleCompanyMode,
        isCompanyOnlyMode,
        selectedCompanies,
        selectedTeams,
        selectedSurveys,
        companies,
    ]);

    /** Informes organizacionales: solo modo empresa completa con ≥2 equipos con encuesta. */
    const canExportOrgReports = useMemo(() => {
        if (!isCompanyOnlyMode) return false;
        return teamSurveyPairs.length >= 2;
    }, [isCompanyOnlyMode, teamSurveyPairs]);

    const canExportTeamReport =
        selectedCompanies.length === 1 &&
        selectedTeams.length === 1 &&
        selectedSurveys.length === 1;

    const canExportBetaIA = canExportTeamReport;

    const updateExportMenuPosition = () => {
        const trigger = exportMenuTriggerRef.current;
        if (!trigger) return;
        const rect = trigger.getBoundingClientRect();
        const gap = 8;
        const menuWidth = Math.min(360, window.innerWidth - 16);
        const spaceAbove = rect.top - gap - 8;
        const spaceBelow = window.innerHeight - rect.bottom - gap - 8;
        const openUp = spaceAbove >= spaceBelow;
        const maxHeight = Math.max(160, Math.min(openUp ? spaceAbove : spaceBelow, window.innerHeight * 0.75));
        let left = rect.right - menuWidth;
        left = Math.max(8, Math.min(left, window.innerWidth - menuWidth - 8));
        const style = {
            position: "fixed",
            left: `${left}px`,
            width: `${menuWidth}px`,
            maxHeight: `${maxHeight}px`,
            zIndex: 1000,
        };
        if (openUp) {
            style.bottom = `${window.innerHeight - rect.top + gap}px`;
            style.top = "auto";
        } else {
            style.top = `${rect.bottom + gap}px`;
            style.bottom = "auto";
        }
        setExportMenuPanelStyle(style);
    };

    const toggleExportMenu = () => {
        if (exportMenuOpen) {
            setExportMenuOpen(false);
            return;
        }
        updateExportMenuPosition();
        setExportMenuOpen(true);
    };

    useEffect(() => {
        if (!exportMenuOpen) return;
        updateExportMenuPosition();
        const onPointerDown = (event) => {
            if (!exportMenuRef.current?.contains(event.target)) {
                setExportMenuOpen(false);
            }
        };
        const onKeyDown = (event) => {
            if (event.key === "Escape") setExportMenuOpen(false);
        };
        const onReposition = () => updateExportMenuPosition();
        document.addEventListener("mousedown", onPointerDown);
        document.addEventListener("keydown", onKeyDown);
        window.addEventListener("resize", onReposition);
        window.addEventListener("scroll", onReposition, true);
        return () => {
            document.removeEventListener("mousedown", onPointerDown);
            document.removeEventListener("keydown", onKeyDown);
            window.removeEventListener("resize", onReposition);
            window.removeEventListener("scroll", onReposition, true);
        };
    }, [exportMenuOpen]);

    // Si la selección deja de ser válida durante un export multi-equipos, cancelar (evita bug al cambiar a 1 equipo/encuesta)
    useEffect(() => {
        if (canExportMultiTeams) return;
        if (!multiTeamsExportRef.current) return;
        multiTeamsExportRef.current = null;
        setCapturingChart(false);
        setCapturingBarEs(false);
        setCapturingBarEn(false);
        setCapturingRadar(false);
        setCapturingIndexEs(false);
        setCapturingIndexEn(false);
        setBarBlobEs(null);
        setBarBlobEn(null);
        setRadarBlob(null);
        setIndexBlobEs(null);
        setIndexBlobEn(null);
        setDashboardData(null);
        setLoadingModalVisible(false);
        setLoadingMessage("");
    }, [canExportMultiTeams]);

    // Nuevos estados para las opciones de exportación
    const [exportOptions, setExportOptions] = useState({
        tipoDatos: 'conNombre', // 'conNombre' o 'anonimo'
        nivelDatos: 'ambos', // 'personal', 'equipo' o 'ambos'
        calcularMedia: false,
        ordenarPor: 'pregunta', // 'pregunta' o 'puntuacion'
        orden: 'ascendente' // 'ascendente' o 'descendente'
    });

    const fetchApiData = (url, executeRef) => {
        const apiUrl = process.env.REACT_APP_API_URL || "https://api.systemicsurvey.com";
        executeRef.current(`${apiUrl}${url}`, "GET");
    };

    useEffect(() => {
        fetchApiData("/gestionCoach/getEmpresas", executeRef);
    }, []);

    useEffect(() => {
        if (data) {
            setCompanies(data);
            // Asignar colores pastel a cada empresa al azar
            const colors = {};
            data.forEach((company) => {
                colors[company.id_empresa] = getRandomPastelColor();
            });
            setCompanyColors(colors);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data]);

    const pastelColors = [
        "#FFB3BA", // Rosa suave
        "#FFDFBA", // Melocotón
        "#FFFFBA", // Amarillo claro
        "#BAFFC9", // Verde menta
        "#BAE1FF", // Azul cielo
        "#D7BDE2", // Lavanda
        "#F5CBA7", // Naranja pastel
        "#AED6F1", // Azul pastel
        "#A2D9CE", // Verde agua
        "#F9E79F", // Amarillo cálido
        "#FFC0CB", // Rosa claro
        "#FFE4E1", // Rosa niebla
        "#E6E6FA", // Lavanda claro
        "#FFFACD", // Amarillo limón
        "#FAFAD2", // Amarillo luz dorada
        "#D1F2EB", // Verde agua suave
        "#D6EAF8", // Azul nieve
        "#FADBD8", // Rosa coral suave
        "#FDEBD0", // Melón pastel
        "#FAD7A0", // Durazno suave
    ];

    // Lista de colores disponibles
    let availableColors = [...pastelColors];

    // Función para asignar un color pastel sin repetir
    const getRandomPastelColor = () => {
        if (availableColors.length === 0) {
            // Reiniciar la lista de colores cuando se acaben
            availableColors = [...pastelColors];
        }
        const randomIndex = Math.floor(Math.random() * availableColors.length);
        const selectedColor = availableColors[randomIndex];
        // Eliminar el color de la lista disponible
        availableColors.splice(randomIndex, 1);
        return selectedColor;
    };

    const handleCompanySelect = (companyId) => {
        let nextCompanies;
        if (selectedCompanies.includes(companyId)) {
            nextCompanies = selectedCompanies.filter((id) => id !== companyId);
            setSelectedTeams(
                selectedTeams.filter((teamId) =>
                    companies
                        .find((company) => company.id_empresa === companyId)
                        ?.equipos.every((team) => team.id_equipo !== teamId)
                )
            );
            setManualTeamSelection(false);
        } else {
            nextCompanies = [...selectedCompanies, companyId];
        }
        setSelectedCompanies(nextCompanies);

        if (nextCompanies.length === 1) {
            setManualTeamSelection(false);
            setSelectedTeams([]);
            setSelectedSurveys([]);
        } else {
            setSelectedSurveys([]);
            if (nextCompanies.length === 0) {
                setSelectedTeams([]);
                setManualTeamSelection(false);
            }
        }
    };

    const syncSurveysForSelectedTeams = (teamIds) => {
        setSelectedSurveys((prev) =>
            teamIds
                .map((tid) => {
                    const team = findTeamById(tid);
                    if (!team?.encuestas?.length) return null;
                    const teamSurveyIds = new Set(team.encuestas.map((e) => e.id_encuesta));
                    const existing = prev.find((id) => teamSurveyIds.has(id));
                    return existing ?? getLatestSurvey(team.encuestas)?.id_encuesta ?? null;
                })
                .filter((id) => id != null)
        );
    };

    const getTeamCompanyColor = (teamId) => {
        const companyId = companies.find((c) =>
            c.equipos?.some((t) => t.id_equipo === teamId)
        )?.id_empresa;
        return companyColors[companyId];
    };

    const handleTeamSelect = (teamId) => {
        if (selectedCompanies.length !== 1) {
            const nextTeams = selectedTeams.includes(teamId)
                ? selectedTeams.filter((id) => id !== teamId)
                : [...selectedTeams, teamId];
            setSelectedTeams(nextTeams);
            syncSurveysForSelectedTeams(nextTeams);
            return;
        }

        setManualTeamSelection(true);

        if (selectedTeams.includes(teamId)) {
            const nextTeams = selectedTeams.filter((id) => id !== teamId);
            setSelectedTeams(nextTeams);
            if (nextTeams.length === 0) {
                setManualTeamSelection(false);
                setSelectedSurveys([]);
            } else {
                syncSurveysForSelectedTeams(nextTeams);
            }
        } else {
            const nextTeams = [...selectedTeams, teamId];
            setSelectedTeams(nextTeams);
            syncSurveysForSelectedTeams(nextTeams);
        }
    };

    const handleTeamSurveySelect = (surveyId, teamId) => {
        const team = findTeamById(teamId);
        const teamSurveyIds = team?.encuestas?.map((e) => e.id_encuesta) || [];
        setSelectedSurveys((prev) => [
            ...prev.filter((id) => !teamSurveyIds.includes(id)),
            surveyId,
        ]);
    };

    const handleExportOptionsChange = (option, value) => {
        setExportOptions(prev => {
            const newOptions = { ...prev, [option]: value };

            // Si se activa calcularMedia, forzar tipoDatos a anonimo
            if (option === 'calcularMedia' && value === true) {
                newOptions.tipoDatos = 'anonimo';
            }

            return newOptions;
        });
    };

    const handleExport = async () => {
        try {
            const apiUrl = process.env.REACT_APP_API_URL || "https://api.systemicsurvey.com";
            const response = await fetch(`${apiUrl}/gestionCoach/exportData`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                credentials: "include",
                body: JSON.stringify({
                    empresas: [...selectedCompanies],
                    equipos: [...selectedTeams],
                    encuestas: [...selectedSurveys],
                    options: exportOptions
                }),
            });

            if (!response.ok) {
                throw new Error(t("errorDownloadingFile"));
            }

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;

            const contentDisposition = response.headers.get("content-disposition");
            const filename = contentDisposition ? contentDisposition.split("filename=")[1].replace(/"/g, "") : "archivo.xlsx";

            a.download = filename;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
        } catch (error) {
            console.error("Error al exportar datos:", error);
        }
    };

    const fetchApiData2 = async (url) => {
        try {
            const apiUrl = process.env.REACT_APP_API_URL || "https://api.systemicsurvey.com";
            const pairs = teamSurveyPairs;
            await execute2(`${apiUrl}${url}`, "POST", {
                id_empresa: selectedCompanies[0],
                id_equipo: pairs.map((p) => p.id_equipo),
                id_encuesta: pairs.map((p) => p.id_encuesta),
                pares: pairs,
                idioma: "es",
            });
        } catch (e) {
            console.log("Error en la petición:", e);
        }
    };

    useEffect(() => {
        if (capturingChart) {
            fetchApiData2("/informes/getDashboardData");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [capturingChart]);

    useEffect(() => {
        if (data2 && Object.keys(data2).length > 0) {
            setDashboardData(data2);
        }
    }, [data2]);


    const handleExportDetailedReport = async () => {
        setShowInfoPopup(false);
        if (!canExportLeaderReport) {
            setInfoPopupMessage(t("leaderReport.selectCompanyHint"));
            setShowInfoPopup(true);
            setTimeout(() => setShowInfoPopup(false), 5000);
            return;
        }

        const companyId = selectedCompanies[0];
        const company = companies.find((c) => c.id_empresa === companyId);
        const teamIds = isCompanyOnlyMode
            ? company.equipos.map((t) => t.id_equipo)
            : selectedTeams;
        const exportPairs = resolveTeamSurveyPairs(
            companies,
            companyId,
            teamIds,
            selectedSurveys
        );
        if (!exportPairs.length) {
            setInfoPopupMessage(t("leaderReport.selectCompanyHint"));
            setShowInfoPopup(true);
            setTimeout(() => setShowInfoPopup(false), 5000);
            return;
        }

        setDashboardData(null);
        setLoadingModalVisible(true);
        setLoadingMessage(t("generatingReport"));

        try {
            const apiUrl = process.env.REACT_APP_API_URL || "https://api.systemicsurvey.com";
            const timestamp = new Date().getTime();
            const esModoEmpresa = isCompanyOnlyMode || exportPairs.length >= 2;

            const response = await fetch(`${apiUrl}/informes/informeDetallado?t=${timestamp}`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                credentials: "include",
                body: JSON.stringify({
                    id_empresa: companyId,
                    id_equipo: esModoEmpresa
                        ? exportPairs.map((p) => p.id_equipo)
                        : exportPairs[0].id_equipo,
                    id_encuesta: esModoEmpresa
                        ? exportPairs.map((p) => p.id_encuesta)
                        : exportPairs[0].id_encuesta,
                    pares: exportPairs,
                    modo_empresa: esModoEmpresa,
                }),
            });

            if (!response.ok) {
                let errorMsg = t("errorDownloadingDetailedReport");
                try {
                    const errJson = await response.json();
                    if (errJson?.message) errorMsg = errJson.message;
                } catch (_) {}
                throw new Error(errorMsg);
            }

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;

            const contentDisposition = response.headers.get("content-disposition");
            const filename = contentDisposition ? contentDisposition.split("filename=")[1].replace(/"/g, "") : "informe_detallado.pdf";

            a.download = filename;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            // Limpiar la URL del blob para evitar memory leaks
            window.URL.revokeObjectURL(url);

            // Ocultar modal después de la descarga
            setLoadingModalVisible(false);
            setLoadingMessage("");
        } catch (error) {
            console.error("Error al exportar informe detallado:", error);
            setLoadingModalVisible(false);
            setLoadingMessage("");
            setInfoPopupMessage(error.message || t("errorDownloadingDetailedReport"));
            setShowInfoPopup(true);
            setTimeout(() => setShowInfoPopup(false), 5000);
        }
    }

    const getOrgExportPairs = () => {
        if (!canExportOrgReports) return [];
        const companyId = selectedCompanies[0];
        const company = companies.find((c) => c.id_empresa === companyId);
        if (!company?.equipos?.length) return [];
        const teamIds = company.equipos.map((t) => t.id_equipo);
        return resolveTeamSurveyPairs(companies, companyId, teamIds, selectedSurveys);
    };

    const LANG_FLAG = { ES: "🇪🇸", EN: "🇬🇧" };

    /** Badge de idioma(s) de descarga: string ("ES"|"EN") o array dual (["ES","EN"]). */
    const langBadge = (langs, hintKey) => {
        const list = (Array.isArray(langs) ? langs : [langs])
            .map((l) => normalizeSurveyLang(l) || l)
            .filter((l) => l === "ES" || l === "EN");
        if (!list.length) return null;
        const unique = [...new Set(list)];
        const title =
            hintKey
                ? t(`exportMenu.${hintKey}`)
                : unique.length > 1
                  ? t("exportMenu.langHintDual")
                  : t(`exportMenu.langHint${unique[0]}`);
        return (
            <span className={styles.exportLangBadge} title={title} aria-label={title}>
                {unique.map((lang, i) => (
                    <React.Fragment key={lang}>
                        {i > 0 && <span className={styles.exportLangSep} aria-hidden="true">·</span>}
                        <span className={styles.exportLangFlag} aria-hidden="true">
                            {LANG_FLAG[lang]}
                        </span>
                        <span className={styles.exportLangCode}>{lang}</span>
                    </React.Fragment>
                ))}
            </span>
        );
    };

    const handleExportOrgReport = async (kind) => {
        setExportMenuOpen(false);
        setShowInfoPopup(false);
        if (!canExportOrgReports) {
            setInfoPopupMessage(t("exportMenu.orgHint"));
            setShowInfoPopup(true);
            setTimeout(() => setShowInfoPopup(false), 5000);
            return;
        }

        const companyId = selectedCompanies[0];
        const exportPairs = getOrgExportPairs();
        if (exportPairs.length < 2) {
            setInfoPopupMessage(t("exportMenu.orgHint"));
            setShowInfoPopup(true);
            setTimeout(() => setShowInfoPopup(false), 5000);
            return;
        }

        const titleByKind = {
            informe: t("exportMenu.orgProgress.titleInforme"),
            guia: t("exportMenu.orgProgress.titleGuia"),
            presentacion: t("exportMenu.orgProgress.titlePresentacion"),
        };
        if (!titleByKind[kind]) return;
        const fallbackFilenameByKind = {
            presentacion: "Presentacion_Ejecutiva.pptx",
            guia: "Guia_Metodologia_dual.zip",
            informe: "Informe_Organizacional.docx",
        };

        const reportLang =
            kind === "informe"
                ? "ES"
                : kind === "presentacion"
                  ? "EN"
                  : "ES";

        setLoadingModalVisible(true);
        setLoadingMessage(titleByKind[kind] || t("exportMenu.generatingOrgReport"));
        setLoadingDetail(t("exportMenu.orgProgress.starting"));
        setLoadingStep(0);
        setLoadingStepTotal(0);

        try {
            const apiUrl = process.env.REACT_APP_API_URL || "https://api.systemicsurvey.com";
            const { blob, contentDisposition } = await downloadOrgReportWithProgress({
                apiUrl,
                kind,
                body: {
                    id_empresa: companyId,
                    id_equipo: exportPairs.map((p) => p.id_equipo),
                    id_encuesta: exportPairs.map((p) => p.id_encuesta),
                    pares: exportPairs,
                    modo_empresa: true,
                    idioma: reportLang,
                    use_ia: orgReportUseAi,
                },
                onProgress: ({ message, step, total }) => {
                    setLoadingDetail(message);
                    setLoadingStep(step || 0);
                    setLoadingStepTotal(total || 0);
                },
            });

            const filename = filenameFromContentDisposition(
                contentDisposition,
                fallbackFilenameByKind[kind]
            );
            triggerBlobDownload(blob, filename);
            setLoadingModalVisible(false);
            setLoadingMessage("");
            setLoadingDetail("");
            setLoadingStep(0);
            setLoadingStepTotal(0);
        } catch (error) {
            console.error("Error al exportar informe organizacional:", error);
            setLoadingModalVisible(false);
            setLoadingMessage("");
            setLoadingDetail("");
            setLoadingStep(0);
            setLoadingStepTotal(0);
            setInfoPopupMessage(error.message || t("exportMenu.errorOrgReport"));
            setShowInfoPopup(true);
            setTimeout(() => setShowInfoPopup(false), 5000);
        }
    };

    const handleMultiTeamsReportConfirm = ({ nombreEquipo, nombreResponsable }) => {
        if (!canExportMultiTeams) {
            setMultiTeamsPopupOpen(false);
            return;
        }
        multiTeamsExportRef.current = { nombreEquipo, nombreResponsable };
        setMultiTeamsPopupOpen(false);
        setLoadingModalVisible(true);
        setLoadingMessage(t("generatingReport"));
        setDashboardData(null);
        setBarBlobEs(null);
        setBarBlobEn(null);
        setRadarBlob(null);
        setIndexBlobEs(null);
        setIndexBlobEn(null);
        setCapturingBarEs(false);
        setCapturingBarEn(false);
        setCapturingRadar(false);
        setCapturingIndexEs(false);
        setCapturingIndexEn(false);
        setCapturingChart(false);
        requestAnimationFrame(() => setCapturingChart(true));
    };

    // Nueva función para exportar informe simple con ambos gráficos
    const handleExportSimpleReport = () => {
        if (selectedCompanies.length !== 1 || selectedTeams.length !== 1 || selectedSurveys.length !== 1) {
            setShowInfoPopup(true);
            return;
        }
        // Validación comentada: permitir exportar incluso si la encuesta está activa
        // if (isSelectedSurveyActive()) {
        //     setShowInfoPopup(true);
        //     setTimeout(() => setShowInfoPopup(false), 5000);
        //     return;
        // }
        // Mostrar modal de carga
        setLoadingModalVisible(true);
        setLoadingMessage(t("generatingReport"));
        // Limpiar datos anteriores y blobs para forzar una nueva petición
        setDashboardData(null);
        setBarBlobEs(null);
        setBarBlobEn(null);
        setRadarBlob(null);
        setIndexBlobEs(null);
        setIndexBlobEn(null);
        setCapturingBarEs(false);
        setCapturingBarEn(false);
        setCapturingRadar(false);
        setCapturingIndexEs(false);
        setCapturingIndexEn(false);
        // Reseteamos capturingChart para asegurar que el useEffect se ejecute de nuevo
        setCapturingChart(false);
        // Usar requestAnimationFrame para asegurar que el estado se actualice antes de la siguiente línea
        requestAnimationFrame(() => {
            setCapturingChart(true);
        });
    };

    // Cuando llegan los datos del dashboard y se está esperando la captura, activa la captura
    useEffect(() => {
        if (capturingChart && dashboardData && Object.keys(dashboardData).length > 0) {
            setCapturingBarEs(true);
            setCapturingBarEn(true);
            setCapturingRadar(true);
            setCapturingIndexEs(true);
            setCapturingIndexEn(true);
            setCapturingChart(false); // Resetea el flag
        }
    }, [dashboardData, capturingChart]);

    // Función para formatear fecha de YYYY-MM-DD a DD/MM/YYYY
    const formatDate = (dateString) => {
        if (!dateString) return '';
        const [year, month, day] = dateString.split('-');
        return `${day}/${month}/${year}`;
    };

    // Cuando todos los blobs estén listos, envía al backend (informe simple o multi-teams)
    const submitInformeExport = useCallback(() => {
        if (!barBlobEs || !barBlobEn || !radarBlob || !indexBlobEs || !indexBlobEn) return;

        const getAccessTokenFromCookies = () => {
            const match = document.cookie.match(/(?:^|; )accessToken=([^;]*)/);
            return match ? decodeURIComponent(match[1]) : null;
        };
        const accessToken = getAccessTokenFromCookies();
        const isMultiTeams = multiTeamsExportRef.current;

        const formData = new FormData();
        formData.append("chartBarEs", barBlobEs, "chartBarEs.png");
        formData.append("chartBarEn", barBlobEn, "chartBarEn.png");
        formData.append("chartRadar", radarBlob, "chartRadar.png");
        formData.append("chartIndexEs", indexBlobEs, "chartIndexEs.png");
        formData.append("chartIndexEn", indexBlobEn, "chartIndexEn.png");

        if (isMultiTeams) {
            formData.append("id_empresa", selectedCompanies[0]);
            formData.append("id_equipo", JSON.stringify(teamSurveyPairs.map((p) => p.id_equipo)));
            formData.append("id_encuesta", JSON.stringify(teamSurveyPairs.map((p) => p.id_encuesta)));
            formData.append("pares", JSON.stringify(teamSurveyPairs));
            formData.append("nombre_equipo", isMultiTeams.nombreEquipo || "");
            formData.append("nombre_responsable", isMultiTeams.nombreResponsable || "");
        } else {
            formData.append("id_empresa", selectedCompanies[0]);
            formData.append("id_equipo", selectedTeams[0]);
            formData.append("id_encuesta", selectedSurveys[0]);
        }

        if (dashboardData && dashboardData.top5 && dashboardData.bottom5) {
            formData.append("top5", JSON.stringify(dashboardData.top5));
            formData.append("bottom5", JSON.stringify(dashboardData.bottom5));
        }

        setLoadingMessage(t("exportingReport"));

        const url = `${process.env.REACT_APP_API_URL || "https://api.systemicsurvey.com"}/informes/${isMultiTeams ? "informeSimpleMultiTeams" : "informeSimple"}`;
        fetch(url, {
            method: "POST",
            headers: accessToken ? { 'Authorization': `Bearer ${accessToken}` } : {},
            body: formData,
            credentials: 'include',
        })
            .then(async (res) => {
                if (res.ok) {
                    setLoadingMessage(t("finalizingDownload"));
                    const blob = await res.blob();
                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement("a");
                    const contentDisposition = res.headers.get("content-disposition");
                    let filename = isMultiTeams ? "Multi-teams_Report_dual.zip" : "Team Report_dual.zip";
                    if (contentDisposition && contentDisposition.includes("filename=")) {
                        filename = contentDisposition.split("filename=")[1].replace(/"/g, "");
                    }
                    a.href = url;
                    a.download = filename;
                    document.body.appendChild(a);
                    a.click();
                    a.remove();
                    setTimeout(() => {
                        setLoadingModalVisible(false);
                        setLoadingMessage("");
                    }, 500);
                } else {
                    setLoadingModalVisible(false);
                    setLoadingMessage("");
                    let errorMsg = t("errorGeneratingReport");
                    try {
                        const errJson = await res.json();
                        if (errJson && errJson.message) {
                            errorMsg = errJson.message;
                            console.error("[Export informe] Servidor:", errJson);
                        }
                    } catch (_) {}
                    alert(errorMsg);
                }
            })
            .catch((err) => {
                setLoadingModalVisible(false);
                setLoadingMessage("");
                console.error("[Export informe] Error de red:", err);
                alert(t("networkErrorGeneratingReport"));
            })
            .finally(() => {
                if (isMultiTeams) multiTeamsExportRef.current = null;
            });

        setBarBlobEs(null);
        setBarBlobEn(null);
        setRadarBlob(null);
        setIndexBlobEs(null);
        setIndexBlobEn(null);
        setCapturingBarEs(false);
        setCapturingBarEn(false);
        setCapturingRadar(false);
        setCapturingIndexEs(false);
        setCapturingIndexEn(false);
    }, [
        barBlobEs,
        barBlobEn,
        radarBlob,
        indexBlobEs,
        indexBlobEn,
        dashboardData,
        selectedCompanies,
        selectedSurveys,
        selectedTeams,
        t,
        teamSurveyPairs,
    ]);

    useEffect(() => {
        submitInformeExport();
    }, [submitInformeExport]);


    return (
        <div className={mainStyle.main}>
            <SideBar selectedCompany={selectedCompany} />
            <div className={`${mainStyle.central} ${styles.page}`}>
                {showInfoPopup && infoPopupMessage && (
                    <InfoPopup message={infoPopupMessage} />
                )}
                <h1 className={styles.tituloEmpresas}>{t("database")}</h1>
                <div className={styles.container}>
                    <div className={styles.column}>
                        <div className={styles.columnHeader}>
                            <h2>{t("empresas")}</h2>
                        </div>
                        <div className={styles.columnList}>
                        {companies.length > 0 ? (
                            companies.map((company) => (
                                <div
                                    key={company.id_empresa}
                                    className={styles.item}
                                    style={{ backgroundColor: companyColors[company.id_empresa], cursor: 'pointer' }}
                                    onClick={() => handleCompanySelect(company.id_empresa)}
                                >
                                    <input
                                        type="checkbox"
                                        checked={selectedCompanies.includes(company.id_empresa)}
                                        onChange={() => handleCompanySelect(company.id_empresa)}
                                        onClick={e => e.stopPropagation()}
                                    />
                                    <label style={{ cursor: 'pointer' }}>{company.nombre}</label>
                                </div>
                            ))
                        ) : (
                            <p className={styles.message}>{t("noCompanies")}</p>
                        )}
                        </div>
                    </div>

                    {/* Lista de Equipos */}
                    <div className={styles.column}>
                        <div className={styles.columnHeader}>
                            <h2>{t("equipos")}</h2>
                        </div>
                        <div className={styles.columnList}>
                        {selectedCompanies.length > 0 ? (
                            selectedCompanies.flatMap((companyId) =>
                                companies
                                    .find((company) => company.id_empresa === companyId)
                                    ?.equipos.map((team) => (
                                        <div
                                            key={team.id_equipo}
                                            className={styles.item}
                                            style={{ backgroundColor: companyColors[companyId], cursor: 'pointer' }}
                                            onClick={() => handleTeamSelect(team.id_equipo)}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={selectedTeams.includes(team.id_equipo)}
                                                onChange={() => handleTeamSelect(team.id_equipo)}
                                                onClick={e => e.stopPropagation()}
                                            />
                                            <label style={{ cursor: 'pointer' }}>{team.nombre}</label>
                                        </div>
                                    ))
                            )
                        ) : (
                            <p className={styles.message}>{t("noTeams")}</p>
                        )}
                        </div>
                    </div>
                    {/* Encuestas por equipo */}
                    <div className={styles.column}>
                        <div className={styles.columnHeader}>
                            <h2>
                                {selectedTeams.length > 0 || isCompanyOnlyMode
                                    ? t("multiTeamsReport.surveyPerTeam")
                                    : t("encuestas")}
                            </h2>
                        </div>
                        <div className={styles.columnList}>
                        {(selectedTeams.length > 0 || isCompanyOnlyMode) ? (
                            <TeamSurveySelector
                                teamIds={isCompanyOnlyMode ? effectiveTeamIds : selectedTeams}
                                findTeamById={findTeamById}
                                selectedSurveys={
                                    isCompanyOnlyMode
                                        ? teamSurveyPairs.map((p) => p.id_encuesta)
                                        : selectedSurveys
                                }
                                getCompanyColor={getTeamCompanyColor}
                                formatDate={formatDate}
                                onSelectSurvey={handleTeamSurveySelect}
                            />
                        ) : (
                            <p className={styles.message}>{t("noSurveys")}</p>
                        )}
                        </div>
                    </div>
                </div>
                <div className={styles.downloadButtonsExport}>
                    <div className={styles.exportMenuWrap} ref={exportMenuRef}>
                        <button
                            type="button"
                            ref={exportMenuTriggerRef}
                            className={styles.exportMenuTrigger}
                            onClick={toggleExportMenu}
                            aria-haspopup="menu"
                            aria-expanded={exportMenuOpen}
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" aria-hidden="true">
                                <path d="M5 20h14v-2H5v2zm7-18L5.33 9h3.84v4h5.66V9h3.84L12 2z" />
                            </svg>
                            {t("exportMenu.title")}
                        </button>
                        {exportMenuOpen && (
                            <div
                                className={styles.exportMenuPanel}
                                role="menu"
                                style={exportMenuPanelStyle}
                            >
                                <div className={styles.exportMenuGroupTitle}>{t("exportMenu.groupTeam")}</div>
                                <button
                                    type="button"
                                    role="menuitem"
                                    className={`${styles.exportMenuItem} ${!canExportTeamReport ? styles.exportMenuItemDisabled : ""}`}
                                    disabled={!canExportTeamReport}
                                    onClick={() => {
                                        setExportMenuOpen(false);
                                        handleExportSimpleReport();
                                    }}
                                >
                                    <span className={styles.exportMenuItemIcon} aria-hidden="true">
                                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"><path d="M3 13h2v8H3v-8zm4-6h2v14H7V7zm4-4h2v18h-2V3zm4 8h2v10h-2V11zm4-3h2v13h-2V8z"/></svg>
                                    </span>
                                    <span className={styles.exportMenuItemLabel}>{t("exportSimpleReport")}</span>
                                    {langBadge(["ES", "EN"])}
                                </button>
                                <button
                                    type="button"
                                    role="menuitem"
                                    className={`${styles.exportMenuItem} ${!canExportMultiTeams ? styles.exportMenuItemDisabled : ""}`}
                                    disabled={!canExportMultiTeams}
                                    title={t("multiTeamsReport.buttonTitle")}
                                    onClick={() => {
                                        if (!canExportMultiTeams) return;
                                        setExportMenuOpen(false);
                                        setMultiTeamsPopupOpen(true);
                                    }}
                                >
                                    <span className={styles.exportMenuItemIcon} aria-hidden="true">
                                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"><path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5s-3 1.34-3 3 1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5C15 14.17 10.33 13 8 13zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z"/></svg>
                                    </span>
                                    <span className={styles.exportMenuItemLabel}>{t("multiTeamsReport.button")}</span>
                                    {langBadge(["ES", "EN"])}
                                </button>
                                <button
                                    type="button"
                                    role="menuitem"
                                    className={`${styles.exportMenuItem} ${!canExportLeaderReport ? styles.exportMenuItemDisabled : ""}`}
                                    disabled={!canExportLeaderReport}
                                    title={t("leaderReport.buttonTitle")}
                                    onClick={() => {
                                        setExportMenuOpen(false);
                                        handleExportDetailedReport();
                                    }}
                                >
                                    <span className={styles.exportMenuItemIcon} aria-hidden="true">
                                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6zm1 7V3.5L19.5 9H15zM8 13h8v2H8v-2zm0 4h8v2H8v-2zm0-8h5v2H8V9z"/></svg>
                                    </span>
                                    <span className={styles.exportMenuItemLabel}>{t("exportDetailedReport")}</span>
                                    {langBadge(["ES", "EN"])}
                                </button>

                                <div className={styles.exportMenuDivider} />
                                <div className={styles.exportMenuGroupHeader}>
                                    <div className={styles.exportMenuGroupTitle}>{t("exportMenu.groupOrg")}</div>
                                    <span
                                        className={styles.exportBetaBadge}
                                        title={t("exportMenu.orgBetaNote")}
                                    >
                                        {t("exportMenu.orgBetaBadge")}
                                    </span>
                                </div>
                                <div
                                    className={styles.exportMenuOption}
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    <div className={styles.exportMenuOptionMain}>
                                        <span className={styles.exportMenuOptionLabel}>
                                            {t("exportMenu.orgUseAiShort")}
                                        </span>
                                        <button
                                            type="button"
                                            role="switch"
                                            aria-checked={orgReportUseAi}
                                            aria-label={t("exportMenu.orgUseAi")}
                                            className={`${styles.exportMenuSwitch} ${orgReportUseAi ? styles.exportMenuSwitchOn : ""}`}
                                            onClick={() => {
                                                const next = !orgReportUseAi;
                                                setOrgReportUseAi(next);
                                                try {
                                                    localStorage.setItem("orgReportUseAi", next ? "1" : "0");
                                                } catch (_) {
                                                    /* ignore */
                                                }
                                            }}
                                        >
                                            <span className={styles.exportMenuSwitchThumb} />
                                        </button>
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    role="menuitem"
                                    className={`${styles.exportMenuItem} ${!canExportOrgReports ? styles.exportMenuItemDisabled : ""}`}
                                    disabled={!canExportOrgReports}
                                    title={t("exportMenu.orgHint")}
                                    onClick={() => handleExportOrgReport("informe")}
                                >
                                    <span className={styles.exportMenuItemIcon} aria-hidden="true">
                                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6zm4 18H6V4h7v5h5v11z"/></svg>
                                    </span>
                                    <span className={styles.exportMenuItemLabel}>{t("exportMenu.orgInforme")}</span>
                                    {langBadge("ES", "langHintInformeES")}
                                </button>
                                <button
                                    type="button"
                                    role="menuitem"
                                    className={`${styles.exportMenuItem} ${!canExportOrgReports ? styles.exportMenuItemDisabled : ""}`}
                                    disabled={!canExportOrgReports}
                                    title={t("exportMenu.orgHint")}
                                    onClick={() => handleExportOrgReport("guia")}
                                >
                                    <span className={styles.exportMenuItemIcon} aria-hidden="true">
                                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"><path d="M18 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2zM6 4h5v8l-2.5-1.5L6 12V4z"/></svg>
                                    </span>
                                    <span className={styles.exportMenuItemLabel}>{t("exportMenu.orgGuia")}</span>
                                    {langBadge(["ES", "EN"], "langHintGuiaDual")}
                                </button>
                                <button
                                    type="button"
                                    role="menuitem"
                                    className={`${styles.exportMenuItem} ${!canExportOrgReports ? styles.exportMenuItemDisabled : ""}`}
                                    disabled={!canExportOrgReports}
                                    title={t("exportMenu.orgHint")}
                                    onClick={() => handleExportOrgReport("presentacion")}
                                >
                                    <span className={styles.exportMenuItemIcon} aria-hidden="true">
                                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"><path d="M21 3H3c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h7v2H8v2h8v-2h-2v-2h7c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 14H3V5h18v12z"/></svg>
                                    </span>
                                    <span className={styles.exportMenuItemLabel}>{t("exportMenu.orgPresentacion")}</span>
                                    {langBadge("EN", "langHintPresentacionEN")}
                                </button>

                                <div className={styles.exportMenuDivider} />
                                <div className={styles.exportMenuGroupTitle}>{t("exportMenu.groupData")}</div>
                                <button
                                    type="button"
                                    role="menuitem"
                                    className={styles.exportMenuItem}
                                    onClick={() => {
                                        setExportMenuOpen(false);
                                        setExportExcelPopupOpen(true);
                                    }}
                                >
                                    <span className={styles.exportMenuItemIcon} aria-hidden="true">
                                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="#169154"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6zm1.5 14.5L12 18l-3.5-1.5L12 15l3.5 1.5zM13 9V3.5L18.5 9H13z"/></svg>
                                    </span>
                                    {t("export")}
                                </button>

                                <div className={styles.exportMenuDivider} />
                                <div className={styles.exportMenuGroupTitle}>{t("exportMenu.groupIA")}</div>
                                <button
                                    type="button"
                                    role="menuitem"
                                    className={`${styles.exportMenuItem} ${!canExportBetaIA ? styles.exportMenuItemDisabled : ""}`}
                                    disabled={!canExportBetaIA}
                                    onClick={() => {
                                        setExportMenuOpen(false);
                                        setBetaIAPopupOpen(true);
                                    }}
                                >
                                    <span className={styles.exportMenuItemIcon} aria-hidden="true">
                                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="#2563eb"><path d="M12 1L14.09 8.26 22 10l-7.91 1.74L12 19l-2.09-7.26L2 10l7.91-1.74L12 1z"/></svg>
                                    </span>
                                    {t("betaIA.title")}
                                </button>
                            </div>
                        )}
                    </div>

                    {/* ChartToImage ocultos para captura de Team / Multi-teams report */}
                    {capturingBarEs && dashboardData && (
                        <ChartToImage
                            key="bar-es"
                            type="bar"
                            dashboardData={dashboardData}
                            language="es"
                            onCapture={(blob) => {
                                setBarBlobEs(blob);
                                setCapturingBarEs(false);
                            }}
                        />
                    )}
                    {capturingBarEn && dashboardData && (
                        <ChartToImage
                            key="bar-en"
                            type="bar"
                            dashboardData={dashboardData}
                            language="en"
                            onCapture={(blob) => {
                                setBarBlobEn(blob);
                                setCapturingBarEn(false);
                            }}
                        />
                    )}
                    {capturingRadar && dashboardData && (() => {
                        const radarSurveyId = teamSurveyPairs[0]?.id_encuesta ?? selectedSurveys[0];
                        const selectedSurvey = companies
                            .flatMap((c) => c.equipos || [])
                            .flatMap((t) => t.encuestas || [])
                            .find((s) => s.id_encuesta === radarSurveyId);
                        const surveyLanguage = selectedSurvey?.idioma?.toLowerCase() || "es";
                        return (
                            <ChartToImage
                                key="radar"
                                type="radar"
                                dashboardData={dashboardData}
                                language={surveyLanguage}
                                onCapture={(blob) => {
                                    setRadarBlob(blob);
                                    setCapturingRadar(false);
                                }}
                            />
                        );
                    })()}
                    {capturingIndexEs && dashboardData && (
                        <ChartToImage
                            key="index-es"
                            type="index"
                            dashboardData={dashboardData}
                            language="es"
                            onCapture={(blob) => {
                                setIndexBlobEs(blob);
                                setCapturingIndexEs(false);
                            }}
                        />
                    )}
                    {capturingIndexEn && dashboardData && (
                        <ChartToImage
                            key="index-en"
                            type="index"
                            dashboardData={dashboardData}
                            language="en"
                            onCapture={(blob) => {
                                setIndexBlobEn(blob);
                                setCapturingIndexEn(false);
                            }}
                        />
                    )}
                </div>
            </div>
            <LoadingModal
                isVisible={loadingModalVisible}
                message={loadingMessage}
                detail={loadingDetail}
                step={loadingStep}
                stepTotal={loadingStepTotal}
            />
            <BetaIAPopup
                isOpen={betaIAPopupOpen}
                onClose={() => setBetaIAPopupOpen(false)}
                id_empresa={selectedCompanies[0]}
                id_equipo={selectedTeams[0]}
                id_encuesta={selectedSurveys[0]}
            />
            <ExportExcelPopup
                isOpen={exportExcelPopupOpen}
                onClose={() => setExportExcelPopupOpen(false)}
                exportOptions={exportOptions}
                onExportOptionsChange={handleExportOptionsChange}
                onConfirmExport={handleExport}
            />
            <MultiTeamsReportPopup
                isOpen={multiTeamsPopupOpen}
                onClose={() => setMultiTeamsPopupOpen(false)}
                onConfirm={handleMultiTeamsReportConfirm}
            />
        </div>
    );
}

export default Database;
